package com.essntl.features.request.ui.addeditrequest

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.ui.addeditrequest.components.AddEditRequestHeader
import com.essntl.features.request.ui.addeditrequest.components.RequestBaseInformation
import com.essntl.features.request.ui.addeditrequest.components.RequestClientInformation
import com.essntl.features.request.ui.addeditrequest.components.RequestEmailInformation
import com.essntl.features.request.ui.addeditrequest.components.RequestTravelAgentInformation
import com.essntl.uikit.components.button.destructive.EsLargeDestructiveButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberFind
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun AddEditRequestScreen(
    state: AddEditRequestState,
    validation: AddEditRequestValidation,
    onEvent: (AddEditRequestEvent) -> Unit,
    onNavigation: (AddEditRequestNavigation) -> Unit,
    getAllRequest: List<RequestModel>,
    onUpdateRequestStatus: (requestId: String, requestStatus: RequestModel.Status) -> Unit,
) {
    val request =
        rememberFind(
            getAllRequest,
            state.request.value.id,
        ) { req -> req.id }

    val isEdit by remember(state.request.value.id) {
        mutableStateOf(
            state.request.value.id.isNotBlank(),
        )
    }
    var isEditEnabled by remember(state.getRequest.data?.id) { mutableStateOf(false) }
    LaunchedEffect(state.addEditRequest.isSuccess) {
        if (state.addEditRequest.isSuccess) {
            isEditEnabled = false
        }
    }

    LaunchedEffect(state.getRequest.data) {
        validation.addEditRequestForm.reset()
        validation.addEditRequestForm.areas.value = state.getRequest.data?.areas.orEmpty()
        validation.addEditRequestForm.travelAgent.value = state.getRequest.data?.travelAgent?.id.orEmpty()
        validation.addEditRequestForm.title.value = state.getRequest.data?.title.orEmpty()
        validation.addEditRequestForm.dateRange.firstValue = state.getRequest.data?.startDate.orEmpty()
        validation.addEditRequestForm.dateRange.secondValue = state.getRequest.data?.endDate.orEmpty()
    }

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier =
        Modifier
            .fillMaxSize(),
    ) {
        AddEditRequestHeader(
            request = request,
            onEditRequestStatus = { requestId, status ->
                onUpdateRequestStatus(requestId, status)
            },
            isLoading = state.addEditRequest.isLoading,
            enabled =
            if (isEdit) {
                isEditEnabled
            } else {
                true
            },
            modifier =
            Modifier
                .fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(1f.spacing))

        Box(
            modifier =
            Modifier
                .weight(1f),
        ) {
            val lazyListState = rememberLazyListState()

            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(1f.spacing),
                state = lazyListState,
                modifier =
                Modifier
                    .fillMaxSize()
                    .clip(
                        RoundedCornerShape(
                            topStart = 24.dp,
                            topEnd = 24.dp,
                        ),
                    ),
            ) {
                if (
                    state.getRequest.isLoading ||
                    state.getRequest.isFailure ||
                    (isEdit && state.getRequest.data == null)
                ) {
                    item {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier =
                            Modifier
                                .fillMaxWidth()
                                .height(200.dp)
                                .padding(2.5f.spacing)
                                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                        ) {
                            if (state.getRequest.isLoading) {
                                EsSpinner()
                            } else {
                                Text(
                                    text = "Something goes wrong!",
                                    color = MaterialTheme.esColorScheme.textSecondary,
                                    style = MaterialTheme.esTypography.p1Medium16,
                                )
                            }
                        }
                    }
                } else {
                    item {
                        RequestBaseInformation(
                            state = state,
                            onRequestChange = { onEvent(AddEditRequestEvent.UpdateRequestFields(it)) },
                            enabled = if (isEdit) isEditEnabled else true,
                            validation = validation,
                        )
                    }

                    item {
                        RequestClientInformation(
                            state = state,
                            onEvent = onEvent,
                            onNavigation = onNavigation,
                            enabled = if (isEdit) isEditEnabled else true,
                        )
                    }

                    item {
                        RequestTravelAgentInformation(
                            state = state,
                            validation = validation,
                            onEvent = onEvent,
                            onNavigation = onNavigation,
                            enabled = if (isEdit) isEditEnabled else true,
                        )
                    }

                    if (!state.request.value.htmlEmail.isNullOrEmpty()) {
                        item {
                            RequestEmailInformation(
                                request = state.request.value,
                            )
                        }
                    }
                }
            }

            EsVerticalScrollbar(
                lazyListState = lazyListState,
                modifier =
                Modifier
                    .align(Alignment.CenterEnd)
                    .fillMaxHeight(),
            )
        }

        Spacer(modifier = Modifier.height(1.dp))

        if (request?.status == RequestModel.Status.Pending) {
            Box(
                modifier =
                Modifier
                    .background(MaterialTheme.esColorScheme.bgSecondary)
                    .padding(2.5.spacing),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                    modifier =
                    Modifier
                        .fillMaxWidth(),
                ) {
                    EsLargePrimaryButton(
                        onClick = {
                            onUpdateRequestStatus(request.id, RequestModel.Status.Todo)
                        },
                        text = "Accept",
                        enabled = true,
                        modifier =
                        Modifier
                            .weight(1F),
                    )
                    Spacer(modifier = Modifier.width(width = 1.5.spacing))
                    EsLargeDestructiveButton(
                        onClick = {
                            onUpdateRequestStatus(request.id, RequestModel.Status.Archived)
                        },
                        text = "Disregard",
                        enabled = true,
                        modifier =
                        Modifier
                            .weight(1F),
                    )
                }
            }
        } else {
            if (request?.status == RequestModel.Status.Todo ||
                request?.status == RequestModel.Status.InProgress ||
                request?.status == RequestModel.Status.Archived ||
                !isEdit
            ) {
                Box(
                    modifier =
                    Modifier
                        .background(MaterialTheme.esColorScheme.bgSecondary)
                        .padding(2.5.spacing),
                ) {
                    EsLargePrimaryButton(
                        onClick = {
                            if (isEdit && !isEditEnabled) {
                                isEditEnabled = true
                            } else if (validation.addEditRequestForm.validate()) {
                                if (isEdit) {
                                    onEvent(AddEditRequestEvent.EditRequest)
                                } else {
                                    onEvent(AddEditRequestEvent.AddRequest)
                                }
                            }
                        },
                        text =
                        if (isEdit) {
                            if (isEditEnabled) {
                                "Save changes"
                            } else {
                                "Edit"
                            }
                        } else {
                            "Add new request"
                        },
                        loading = state.addEditRequest.isLoading,
                        enabled =
                        if (isEditEnabled) {
                            state.isEditEnabled && validation.addEditRequestForm.isValid
                        } else {
                            true
                        },
                        modifier = Modifier
                            .fillMaxWidth(),
                    )
                }
            }
        }
    }
}
